import React, { useState, useEffect, useMemo } from 'react';
import Input from '../common/Input';
import Button from '../common/Button';
import { customersAPI } from '../../api/customers';
import { formatCurrency, formatPhone } from '../../utils/formatters';

const PAYMENT_METHODS = [
  { id: 'cash',   label: 'Cash',   icon: '💵' },
  { id: 'card',   label: 'Card',   icon: '💳' },
  { id: 'knet',   label: 'K-Net',  icon: '🏧' },
  { id: 'wallet', label: 'Wallet', icon: '👛' },
];

const PLAN_TYPES = { CREDIT: 'credit', PIECES: 'pieces' };

const PurchaseSubscriptionForm = ({ plans, onSubmit, onCancel }) => {
  // ── Customer ──────────────────────────────────────────────────────────────
  const [phone, setPhone] = useState('');
  const [customer, setCustomer] = useState(null);
  const [matchingCustomers, setMatchingCustomers] = useState([]);
  const [registerName, setRegisterName] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  // ── Plan & payment ────────────────────────────────────────────────────────
  const [planId, setPlanId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [receivedAmount, setReceivedAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});

  const selectedPlan = useMemo(
    () => plans.find(p => p._id === planId) || null,
    [plans, planId]
  );

  // ── Customer lookup by phone ──────────────────────────────────────────────
  useEffect(() => {
    if (!phone || phone.length < 4) {
      setMatchingCustomers([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setSearchLoading(true);
      try {
        const res = await customersAPI.getAll({ search: phone, isActive: true, limit: 10 });
        if (!cancelled) setMatchingCustomers(res.data || []);
      } catch {
        if (!cancelled) setMatchingCustomers([]);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [phone]);

  // Auto-fill received amount with the plan price when payment method is cash/card/knet.
  useEffect(() => {
    if (selectedPlan && paymentMethod !== 'wallet') {
      setReceivedAmount(selectedPlan.price.toString());
    }
  }, [selectedPlan, paymentMethod]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const isExistingCustomer = !!customer;
  const phoneIsValid = /^[0-9]{8}$/.test(phone);
  const walletAvailable = (customer?.walletBalance ?? 0) >= (selectedPlan?.price ?? 0);

  const change = useMemo(() => {
    if (!selectedPlan || paymentMethod === 'wallet') return 0;
    const received = parseFloat(receivedAmount);
    if (Number.isNaN(received)) return 0;
    return Math.max(0, received - selectedPlan.price);
  }, [selectedPlan, receivedAmount, paymentMethod]);

  const validate = () => {
    const next = {};
    if (!selectedPlan) next.plan = 'Select a plan';
    if (!isExistingCustomer && !phoneIsValid) next.phone = 'Enter a valid 8-digit phone number';
    if (!isExistingCustomer && !registerName.trim()) next.registerName = 'Customer name is required to register';

    if (paymentMethod === 'wallet') {
      if (!walletAvailable) next.payment = 'Insufficient wallet balance';
    } else {
      const received = parseFloat(receivedAmount);
      if (!received || received < (selectedPlan?.price || 0)) {
        next.receivedAmount = `Amount must be at least ${formatCurrency(selectedPlan?.price || 0)}`;
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      subscriptionPlanId: planId,
      paymentMethod,
      paymentReference: paymentReference.trim() || undefined,
      notes: notes.trim() || undefined,
    };
    if (isExistingCustomer) payload.customerId = customer._id;
    else {
      payload.customerPhone = phone;
      payload.customerName = registerName.trim();
    }
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="form">

      {/* ── Customer ─────────────────────────────────────────────────────── */}
      <div className="form-section">
        <h4 style={{ marginBottom: 'var(--spacing-md)', color: 'var(--gray-700)' }}>
          Customer
        </h4>

        {customer ? (
          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <div className="om-avatar-sm">{customer.name?.charAt(0).toUpperCase()}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>{customer.name}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>📞 {formatPhone(customer.phone)}</div>
            </div>
            {(customer.walletBalance ?? 0) > 0 && (
              <span className="ps-wallet-chip">
                Wallet: {formatCurrency(customer.walletBalance)}
              </span>
            )}
            <button
              type="button"
              className="ps-clear-customer"
              onClick={() => { setCustomer(null); setPhone(''); }}
            >
              ✕
            </button>
          </div>
        ) : (
          <>
            <div className="form-row">
              <Input
                label="Phone Number"
                name="phone"
                value={phone}
                onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 8))}
                error={errors.phone}
                placeholder="8-digit phone number"
                inputMode="numeric"
                required
              />
            </div>

            {searchLoading && <div className="input-hint">Searching…</div>}

            {matchingCustomers.length > 0 && (
              <div className="ps-customer-dropdown" style={{ position: 'static', marginBottom: 12 }}>
                {matchingCustomers.map(c => (
                  <div
                    key={c._id}
                    className="ps-customer-option"
                    onClick={() => { setCustomer(c); setPhone(c.phone); }}
                  >
                    <strong>{c.name}</strong>
                    <span>{formatPhone(c.phone)}</span>
                    {(c.walletBalance || 0) > 0 && (
                      <span className="ps-wallet-chip">{formatCurrency(c.walletBalance)}</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {phoneIsValid && matchingCustomers.length === 0 && !searchLoading && (
              <>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: -4, marginBottom: 8 }}>
                  No customer with this number — register them now to continue.
                </div>
                <div className="form-row">
                  <Input
                    label="Customer Name"
                    name="registerName"
                    value={registerName}
                    onChange={e => setRegisterName(e.target.value)}
                    error={errors.registerName}
                    placeholder="Full name"
                    required
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* ── Plan ─────────────────────────────────────────────────────────── */}
      <div className="form-section">
        <h4 style={{ marginBottom: 'var(--spacing-md)', color: 'var(--gray-700)' }}>
          Subscription Plan
        </h4>
        <div className="form-row">
          <div className="input-group">
            <label className="input-label">
              Plan <span className="text-danger">*</span>
            </label>
            <select
              className={`input ${errors.plan ? 'input-error' : ''}`}
              value={planId}
              onChange={e => setPlanId(e.target.value)}
              required
            >
              <option value="">— Select a plan —</option>
              {plans.map(plan => (
                <option key={plan._id} value={plan._id}>
                  {plan.name} — {formatCurrency(plan.price)}
                  {plan.planType === PLAN_TYPES.CREDIT
                    ? ` → ${formatCurrency(plan.creditAmount)} credit`
                    : ` → ${plan.pieceLimit} pieces`}
                  {plan.hasExpiry ? ` · ${plan.durationDays}d` : ' · open-ended'}
                </option>
              ))}
            </select>
            {errors.plan && <span className="error-message">{errors.plan}</span>}
          </div>
        </div>

        {selectedPlan && (
          <div className="plan-preview kuwait-plan-preview">
            <div className="plan-preview-header">
              <h4>{selectedPlan.name}</h4>
              <span className="om-badge" style={{ background: '#eef2ff', color: '#3730a3' }}>
                {selectedPlan.planType === PLAN_TYPES.CREDIT ? 'Credit Plan' : 'Pieces Plan'}
              </span>
            </div>
            <div className="plan-preview-content">
              <div className="preview-row">
                <span className="preview-label">Price paid</span>
                <span className="preview-value kuwait-price">{formatCurrency(selectedPlan.price)}</span>
              </div>
              {selectedPlan.planType === PLAN_TYPES.CREDIT ? (
                <>
                  <div className="preview-row highlight">
                    <span className="preview-label">Usable credit</span>
                    <span className="preview-value discount">{formatCurrency(selectedPlan.creditAmount)}</span>
                  </div>
                  {selectedPlan.creditAmount > selectedPlan.price && (
                    <div className="preview-row">
                      <span className="preview-label">Bonus value</span>
                      <span className="preview-value">
                        {formatCurrency(selectedPlan.creditAmount - selectedPlan.price)}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="preview-row highlight">
                  <span className="preview-label">Pieces included</span>
                  <span className="preview-value">{selectedPlan.pieceLimit}</span>
                </div>
              )}
              <div className="preview-row">
                <span className="preview-label">Validity</span>
                <span className="preview-value">
                  {selectedPlan.hasExpiry ? `${selectedPlan.durationDays} day(s)` : 'No expiry'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Payment ──────────────────────────────────────────────────────── */}
      {selectedPlan && (
        <div className="form-section">
          <h4 style={{ marginBottom: 'var(--spacing-md)', color: 'var(--gray-700)' }}>
            Payment
          </h4>

          <div className="ps-method-buttons" style={{ marginBottom: 12 }}>
            {PAYMENT_METHODS.map(method => {
              const disabled = method.id === 'wallet' && !walletAvailable;
              return (
                <button
                  type="button"
                  key={method.id}
                  className={`ps-method-btn ${paymentMethod === method.id ? 'active' : ''}`}
                  onClick={() => !disabled && setPaymentMethod(method.id)}
                  disabled={disabled}
                  title={disabled ? 'Insufficient wallet balance' : ''}
                >
                  {method.icon} {method.label}
                </button>
              );
            })}
          </div>

          {paymentMethod !== 'wallet' && (
            <div className="form-row">
              <Input
                label="Amount Received (KWD)"
                name="receivedAmount"
                type="number"
                step="0.001"
                min="0"
                value={receivedAmount}
                onChange={e => setReceivedAmount(e.target.value)}
                error={errors.receivedAmount}
                placeholder={selectedPlan.price.toFixed(3)}
                required
              />
              {(paymentMethod === 'card' || paymentMethod === 'knet') && (
                <Input
                  label="Reference / Transaction ID"
                  name="paymentReference"
                  value={paymentReference}
                  onChange={e => setPaymentReference(e.target.value)}
                  placeholder="Optional"
                />
              )}
            </div>
          )}

          <div style={{
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: 12,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 13, color: '#64748b' }}>Plan price</span>
              <strong>{formatCurrency(selectedPlan.price)}</strong>
            </div>
            {paymentMethod !== 'wallet' && change > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: '#64748b' }}>Change to return</span>
                <strong style={{ color: '#10b981' }}>{formatCurrency(change)}</strong>
              </div>
            )}
            <div style={{
              borderTop: '1.5px solid #e2e8f0',
              paddingTop: 8,
              marginTop: 8,
              display: 'flex',
              justifyContent: 'space-between',
            }}>
              <span style={{ fontWeight: 700 }}>Total to pay</span>
              <strong style={{ fontSize: 16, color: '#1d4ed8' }}>
                {formatCurrency(selectedPlan.price)}
              </strong>
            </div>
          </div>

          {errors.payment && <div className="error-message" style={{ marginTop: 8 }}>{errors.payment}</div>}
        </div>
      )}

      {/* ── Notes ────────────────────────────────────────────────────────── */}
      <div className="form-row">
        <div className="input-group">
          <label className="input-label">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            className="input"
            rows="2"
            placeholder="Any context the next cashier should know"
          />
        </div>
      </div>

      <div className="form-actions">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary">
          Confirm & Sell
        </Button>
      </div>
    </form>
  );
};

export default PurchaseSubscriptionForm;
